// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { CloseIcon } from "src/assets/icons";
import Walkthrough from "../Walkthrough/Walkthrough";
import {
  MockButton,
  MockDialog,
  MockField,
  MockHubCard,
  MockSelect,
  mockTileBackground,
} from "../GuideMocks/PortalMocks";
import {
  InstalledApps,
  InstallerWelcome,
  QuickLinkMenu,
  UserAccountControl,
} from "../GuideMocks/WindowsMocks";
import { GUIDE_NAMESPACE } from "../GuideText/GuideText";

// Utils
import { WalkthroughStep, guideTarget } from "../Walkthrough/walkthroughState";

const UPDATE_STEPS: Array<WalkthroughStep> = [
  {
    screen: "c",
    target: "chip",
    text: "An orange <b>Update ... available</b> label means a newer hub is out.",
  },
  {
    target: "dl",
    text: "On the hub PC, click the orange download icon. Its tip says <b>Update to</b> and the version.",
  },
  {
    screen: "f",
    target: "kept",
    text: "Run the new file the same way as the first install. The hub keeps everything.",
  },
];

export const UpdateWalkthrough = () => {
  const { t } = useTranslation();

  return (
    <Walkthrough id="update" steps={UPDATE_STEPS}>
      {(view) =>
        view.screen === "c" ? (
          <MockHubCard
            status="Active"
            version="2.4.0"
            update="2.5.0"
            tooltip={{
              on: "download",
              text: t("Update to {{version}}", { version: "2.5.0" }),
            }}
            targets={{ version: "chip", download: "dl" }}
          />
        ) : (
          <InstallerWelcome
            heading="Completed the RetinaLogik EMR Hub Setup Wizard"
            lines={[
              "The hub was upgraded and kept its enrollment. The RetinaLogik EMR Hub service is running.",
            ]}
            lineTarget="kept"
            nextLabel="Finish"
          />
        )
      }
    </Walkthrough>
  );
};

const MANAGE_STEPS: Array<WalkthroughStep> = [
  {
    screen: "c",
    target: "edit",
    text: "Click the pencil to <b>Edit EMR Hub</b>.",
  },
  {
    screen: "e",
    target: "alias",
    text: "Change the <b>Alias</b> if you like.",
  },
  {
    target: "addc",
    show: ["north"],
    text: "To share the hub with another of your clinics, pick it here.",
  },
  {
    target: "clinics",
    text: "The <b>Home Clinic</b> always stays. Remove other clinics with the x.",
  },
  { target: "save", text: "Click <b>Save</b>." },
  {
    screen: "c",
    target: "del",
    show: ["delete-tip"],
    text: "The red bin deletes the hub.",
  },
  {
    screen: "d",
    target: "warn",
    text: "The portal asks you to confirm. This cannot be undone.",
  },
  {
    target: "delbtn",
    text: "Click <b>Delete</b> only if you are sure. Then uninstall the hub from the PC.",
  },
];

export const ManageWalkthrough = () => {
  const { t } = useTranslation();
  const { t: tg } = useTranslation(GUIDE_NAMESPACE);
  const theme = useTheme();

  return (
    <Walkthrough id="manage" steps={MANAGE_STEPS}>
      {(view) => (
        <>
          {view.screen === "c" && (
            <MockHubCard
              status="Active"
              compact
              tooltip={
                view.shows("delete-tip")
                  ? { on: "delete", text: t("Delete EMR Hub") }
                  : { on: "edit", text: t("Edit EMR Hub") }
              }
              targets={{ edit: "edit", delete: "del" }}
            />
          )}
          {view.screen === "e" && (
            <MockDialog
              title={t("Edit EMR Hub")}
              maxWidth="420px"
              footer={
                <>
                  <MockButton kind="cancel">{t("Cancel")}</MockButton>
                  <MockButton kind="confirm" targetId="save">
                    {t("Save")}
                  </MockButton>
                </>
              }
            >
              <MockField
                label={t("Alias")}
                required
                value={tg("Front desk PC")}
                targetId="alias"
              />
              <Box
                {...guideTarget("clinics")}
                display="flex"
                flexDirection="column"
                gap="6px"
              >
                <Typography fontSize="13px">
                  {t("Associated Clinics")}{" "}
                  <Box component="span" color="#DB3131">
                    *
                  </Box>
                </Typography>
                <Box
                  sx={{
                    borderRadius: "4px",
                    padding: "8px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                    fontSize: "13px",
                    backgroundColor: mockTileBackground(theme),
                  }}
                >
                  <b>{`${t("Home Clinic")}: ${tg("Maple Eye Clinic")}`}</b>
                  {view.shows("north") && (
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      {`1. ${tg("Maple Eye Clinic North")}`}
                      <CloseIcon width="16px" height="16px" />
                    </Box>
                  )}
                </Box>
                <MockSelect
                  label=""
                  value=""
                  placeholder={t("Select a value")}
                  filled={false}
                  targetId="addc"
                />
              </Box>
            </MockDialog>
          )}
          {view.screen === "d" && (
            <MockDialog
              title={t("Delete EMR Hub")}
              maxWidth="420px"
              footer={
                <>
                  <MockButton kind="cancel">{t("Cancel")}</MockButton>
                  <MockButton kind="delete" targetId="delbtn">
                    {t("Delete")}
                  </MockButton>
                </>
              }
            >
              <Typography {...guideTarget("warn")} fontSize="14px">
                {t(
                  "Are you sure you want to delete this EMR hub? This action cannot be undone."
                )}
              </Typography>
            </MockDialog>
          )}
        </>
      )}
    </Walkthrough>
  );
};

const UNINSTALL_STEPS: Array<WalkthroughStep> = [
  {
    screen: "menu",
    target: "start",
    text: "On the PC, right-click the <b>Start</b> button (the Windows logo at the bottom of the screen).",
  },
  {
    target: "installed",
    text: "A menu opens. Click <b>Installed apps</b>, the first line.",
  },
  {
    screen: "list",
    target: "search",
    text: "The <b>Installed apps</b> page opens. Click the search box at the top.",
  },
  { on: ["searched"], target: "search", text: "Type <b>Retina</b>." },
  {
    target: "name",
    text: "Check the name says <b>RetinaLogik EMR</b>, by RetinaLogik Inc. That is the old version.",
  },
  {
    target: "dots",
    text: "Click the three dots on the right of that line.",
  },
  { show: ["menu"], target: "uninstall", text: "Click <b>Uninstall</b>." },
  {
    hide: ["menu"],
    show: ["confirm"],
    target: "confirm",
    text: "Windows asks to confirm. Click <b>Uninstall</b> again.",
  },
  {
    screen: "uac",
    target: "yes",
    text: "If Windows asks for permission, click <b>Yes</b>. Wait a moment while it removes the program.",
  },
  {
    screen: "list",
    hide: ["confirm"],
    on: ["removed"],
    target: "search",
    text: "Done. Searching for <b>Retina</b> now shows <b>0 apps found</b>. You can install the new hub.",
  },
];

export const UninstallWalkthrough = () => (
  <Walkthrough id="uninstall" steps={UNINSTALL_STEPS}>
    {(view) => (
      <>
        {view.screen === "menu" && (
          <QuickLinkMenu startTarget="start" installedAppsTarget="installed" />
        )}
        {view.screen === "list" && (
          <InstalledApps
            isSearched={view.isOn("searched")}
            isRemoved={view.isOn("removed")}
            showMenu={view.shows("menu")}
            showConfirm={view.shows("confirm")}
            searchTarget="search"
            nameTarget="name"
            dotsTarget="dots"
            uninstallTarget="uninstall"
            confirmTarget="confirm"
          />
        )}
        {view.screen === "uac" && <UserAccountControl yesTarget="yes" />}
      </>
    )}
  </Walkthrough>
);
